import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XDataView } from '@adornis/data-view/client/legacy/x-data-view.js';
import { html, noChange, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../fonts/fonts.js';
import './tr-data-view-table.js';
import './tr-icon.js';

@customElement('tr-data-view')
export class TrDataView<T extends typeof AdornisEntity> extends XDataView<T> {
  @property({ attribute: true }) coloring?: ColorType;
  @property({ attribute: true }) bordered: boolean | undefined = false;
  @property({ attribute: true }) expandable: boolean | undefined = false;

  @property({ attribute: false }) dragState: boolean | undefined = false;
  @state() private _isExpanded = true;

  get __dvController() {
    return (this as any)._dvController as XDataView<T>['_dvController'];
  }

  protected get _getColoring() {
    return this.coloring ?? this.colors.light;
  }

  protected _renderHeadline() {
    if (!this.expandable) return nothing;

    return html`
      <tr-flex headline horizontal padding="xxs" space-between crossaxis-center>
        <slot></slot>
        ${
          this.expandable
            ? html`
                <tr-icon ${css({ padding: this.spacing.xxs })} @click=${_ => (this._isExpanded = !this._isExpanded)}>
                  ${this._isExpanded ? 'hide' : 'visibility'}</tr-icon
                >
              `
            : html` <tr-flex></tr-flex> `
        }
      </tr-flex>
    `;
  }

  override render() {
    return html`
      <tr-flex wrapper>
        ${this._renderHeadline()}

        <tr-flex content>
          ${
            this._isExpanded && !this.dragState
              ? super.render()
              : html`
                  <tr-flex center crossaxis-center w100 @click=${_ => (this._isExpanded = true)}>
                    <tr-icon>more_horiz</tr-icon>
                  </tr-flex>
                `
          }
          ${
            this.dragState
              ? html`
                  <tr-flex drop-zone-overlay padding="xs" flex>
                    <tr-flex horizontal space="xxs" center crossaxis-center flex>
                      <tr-icon>falling</tr-icon>
                      <tr-h4>Hier fallen lassen</tr-h4>
                    </tr-flex>
                  </tr-flex>
                `
              : nothing
          }
        </tr-flex>
      </tr-flex>
    `;
  }

  override tableView() {
    if (!this.__dvController) return noChange;
    return xComponents(html`
      <tr-data-view-table
        view-table
        .coloring=${this.coloring}
        .data=${this.__dvController.data}
        .fullOptions=${this.fullOptions.value}
        .loading=${this.__dvController.loadingInitially || !this.__dvController.complete}
        @sort=${(e: { detail: { columnFieldName: Maybe<string>; sortingDirection: Maybe<'desc' | 'asc'> } }) => {
          // TODO consolidate and think about where to put this
          const filter = this.fullOptions.value?.view.filter;
          if (!filter || !e.detail.columnFieldName) return;

          const sortingFieldIndex = filter.sortingFields.indexOf(e.detail.columnFieldName);
          // sorting for this fieldName already exists in current filter
          if (sortingFieldIndex > -1) {
            // sorting direction needs to be updated
            if (e.detail.sortingDirection) {
              filter.sortingDirections[sortingFieldIndex] = e.detail.sortingDirection;
              // sorting for this fieldName needs to be removed
            } else {
              filter.sortingFields.splice(sortingFieldIndex, 1);
              filter.sortingDirections.splice(sortingFieldIndex, 1);
            }
            // sorting for this fieldName needs to be added with ascending as default value
          } else {
            filter.sortingFields.unshift(e.detail.columnFieldName);
            filter.sortingDirections.unshift(e.detail.sortingDirection ?? 'asc');
          }

          // default to descending _id sorting if no other sorting is selected to ensure a consistent order
          if (!filter.sortingFields.length) {
            filter.sortingFields.push('_id');
            filter.sortingDirections.push('desc');
          }
        }}
        .renderedIndexCallback=${(num: number) => this.virtualizationOnRender(num)}
      ></tr-data-view-table>
    `);
  }

  override styles() {
    const bordered = this.bordered;
    return [
      ...super.styles(),
      {
        ':host': {
          '[wrapper]': {
            ...(bordered
              ? {
                  backgroundColor: this._getColoring.shade(200),
                  borderRadius: `CALC( ${this.sizes.borderRadius} + ${this.spacing.xxs} - 1px )`,
                  overflow: 'hidden',
                }
              : {}),

            '[headline]': {
              paddingBottom: this.spacing.xxs,

              'tr-h4': {
                lineHeight: this.spacing.md,
                padding: `${this.spacing.xxs} 0 ${this.spacing.xxs} ${this.spacing.xxs}`,
              },
            },

            '[content]': {
              position: 'relative',

              ...(bordered
                ? {
                    backgroundColor: '#fff',
                    borderRadius: this.sizes.borderRadius,
                    overflow: 'hidden',
                    margin: this.spacing.xxs,
                    marginTop: '0',
                  }
                : {}),

              ...(!this._isExpanded
                ? { minHeight: this.spacing.md, backgroundColor: this._getColoring.shade(50).shade(200) }
                : {}),

              ...(this.dragState ? { minHeight: this.spacing.xl } : {}),

              '[view-table]': {},

              '[drop-zone-overlay]': {
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                background: this._getColoring.shade(50).opacity(0.75),
                backdropFilter: 'blur(2px)',
                borderRadius: this.sizes.borderRadius,
                overflow: 'hidden',
                zIndex: '1',

                '> tr-flex': { borderRadius: this.sizes.borderRadius, border: `1px solid ${this._getColoring}` },
              },
            },
          },
        },
      } as Styles,
    ];
  }
}
